import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '../../lib/cn';
import type { InvoiceDocument } from './api';

/**
 * A remittance row you can take with you.
 *
 * ⚠ It lives HERE, in the document, because the hosted page used to draw its
 * own copyable bank block in the page chrome and the document drew a second
 * one about 1,200px below it — the same four lines under the same heading,
 * twice on one screen. The details belong to the document (it is what gets
 * printed, forwarded and filed), so the document keeps them and gained the
 * affordance; the page now points down at them in one sentence.
 *
 * The button is `print:hidden` and appears only where somebody can click it:
 * a printed page with a picture of a copy button on it is noise.
 */
function CopyValue({ label, value }: { label: string | null; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 2000);
        } catch {
          /* the text is right there to select */
        }
      }}
      aria-label={`Copy ${label ?? 'payment detail'}`}
      title={copied ? 'Copied' : 'Copy'}
      className="ml-2 rounded p-1 align-[-3px] text-grey-400 transition-colors hover:bg-grey-100 hover:text-secondary print:hidden"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-status-paid" aria-hidden />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden />
      )}
    </button>
  );
}

/**
 * The client-facing invoice sheet — ONE component, four surfaces: the staff
 * preview, the print portal, the hosted page a client opens from their email,
 * and — through the same `document` payload — the server-typeset PDF.
 * Nothing here may say something the file the client receives does not.
 *
 * ⚠ It lives in its own module rather than inside `InvoiceDetailPage` because
 * the hosted page is PUBLIC: its chunk must contain no auth-store code at all
 * (a client has no session to read, refresh or clear). The only value import
 * here is `cn`; `InvoiceDocument` is imported as a type and erased at build.
 */
export function InvoiceSheet({
  doc,
  hidePayOnline = false,
  copyRemittance = false,
  remittanceId,
}: {
  doc: InvoiceDocument;
  /**
   * The hosted page draws its own LIVE pay button above the sheet, so it
   * suppresses the document's copy — one page must not make the same offer
   * twice. Everywhere the button cannot exist (the staff preview, the print
   * portal, and the PDF this object is typeset into) the document's own link
   * is the only way to pay online, so it stays.
   */
  hidePayOnline?: boolean;
  /**
   * Put a copy button on every bank detail. On for the hosted page a client
   * pays from — transcribing a routing number by hand is where a payment goes
   * to the wrong account — and off for the PDF, the print portal and the staff
   * preview, where there is nothing to click.
   */
  copyRemittance?: boolean;
  /** Anchor id, so a page above the sheet can link down to the bank block
   *  instead of repeating it. Set it only where ONE sheet is on the page. */
  remittanceId?: string;
}) {
  const payOnline = hidePayOnline ? null : doc.payOnline;
  return (
    <>
      <div className="flex items-start justify-between gap-8 border-b-2 border-secondary pb-6">
        <div>
          {doc.company.logoUrl && (
            // Hidden if it fails to load so a broken URL can never leave a
            // placeholder on a client's invoice.
            <img
              src={doc.company.logoUrl}
              alt=""
              className="mb-2 h-11 w-auto max-w-[13rem] object-contain object-left"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          <p className="text-2xl font-black text-secondary">{doc.company.name}</p>
          {[
            doc.company.legalName,
            ...doc.company.addressLines,
            doc.company.contactLine,
            doc.company.website,
            doc.company.taxId,
          ]
            .filter((line): line is string => Boolean(line))
            .map((line) => (
              <p key={line} className="mt-0.5 text-sm text-grey-600">
                {line}
              </p>
            ))}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-3xl font-black uppercase tracking-widest text-secondary">Invoice</p>
          <p className="num mt-1 text-sm font-semibold">#{doc.number}</p>
          {doc.watermark && (
            <p
              className={cn(
                'mt-2 inline-block rounded border px-2 py-0.5 text-xs font-bold uppercase tracking-widest',
                doc.watermark === 'PAID'
                  ? 'border-status-paid text-status-paid'
                  : doc.watermark === 'VOID'
                    ? 'border-status-late text-status-late'
                    : 'border-grey-400 text-grey-500',
              )}
            >
              {doc.watermark}
            </p>
          )}
        </div>
      </div>

      <div className="mt-6 flex justify-between gap-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-grey-500">Bill to</p>
          <p className="mt-1 text-base font-bold text-secondary">{doc.billTo.name}</p>
          {doc.billTo.lines.map((line) => (
            <p key={line} className="text-sm text-grey-700">
              {line}
            </p>
          ))}
          {doc.billTo.taxId && (
            <p className="num mt-0.5 text-sm text-grey-600">{doc.billTo.taxId}</p>
          )}
          {doc.billTo.email && <p className="mt-0.5 text-sm text-grey-600">{doc.billTo.email}</p>}
        </div>
        {/* self-start: a flex child table stretches to the bill-to block's
            height and spreads its own rows apart if you let it. */}
        {/* The amount due (`strong`) is what the client has to act on, so it
            is set like a headline. It used to sit at the same weight and size
            as "Terms" — small grey meta type for the one number on the page
            anybody is going to do anything about. */}
        <table className="num self-start text-right text-sm">
          <tbody>
            {doc.meta.map((row) => (
              <tr key={row.label}>
                <td
                  className={cn(
                    'pr-4 text-left font-semibold',
                    row.strong ? 'pt-2 align-bottom text-secondary' : 'text-grey-500',
                  )}
                >
                  {row.label}
                </td>
                <td
                  className={cn(
                    row.strong && 'pt-2 text-xl font-black leading-tight',
                    row.strong && doc.isLate && 'text-status-late',
                  )}
                >
                  {row.value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {doc.lateNotice && (
        <p className="mt-6 border-l-4 border-status-late bg-status-late-bg px-3 py-2 text-sm font-bold text-status-late">
          {doc.lateNotice}
        </p>
      )}

      {doc.subject && (
        // What the invoice is FOR, above the items — the first thing an AP
        // clerk looks for when matching it to a purchase order.
        <p className="mt-6 border-b border-grey-200 pb-2 text-base font-bold text-secondary">
          {doc.subject}
        </p>
      )}

      <table className="mt-8 w-full border-collapse text-sm">
        <thead>
          <tr className="border-b-2 border-secondary text-left">
            <th className="py-2 pr-2 text-xs font-bold uppercase tracking-wider">Description</th>
            <th className="px-2 py-2 text-right text-xs font-bold uppercase tracking-wider">Qty</th>
            <th className="px-2 py-2 text-right text-xs font-bold uppercase tracking-wider">
              Unit price
            </th>
            <th className="py-2 pl-2 text-right text-xs font-bold uppercase tracking-wider">
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {doc.lines.map((line, index) => (
            <tr key={`${line.description}-${index}`} className="border-b border-grey-200">
              <td className="py-2 pr-2">
                {line.description}
                {line.detail && <span className="text-grey-500"> — {line.detail}</span>}
              </td>
              <td className="num px-2 py-2 text-right">{line.quantity}</td>
              <td className="num px-2 py-2 text-right">{line.unitPrice}</td>
              <td className="num py-2 pl-2 text-right font-semibold">{line.amount}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="invoice-print-totals mt-4 flex justify-end">
        <table className="num w-72 text-right text-sm">
          <tbody>
            {doc.totals.map((total, index) => (
              <tr
                key={`${total.label}-${index}`}
                className={cn(
                  total.kind === 'total' && 'border-t border-secondary',
                  total.kind === 'due' && 'border-t-2 border-secondary',
                )}
              >
                <td
                  className={cn(
                    'py-1 pr-3 text-left',
                    total.kind === 'due'
                      ? 'font-black'
                      : total.kind === 'total'
                        ? 'font-bold'
                        : 'text-xs font-semibold text-grey-500',
                  )}
                >
                  {total.label}
                </td>
                <td
                  className={cn(
                    total.kind === 'due' && 'font-black',
                    total.kind === 'total' && 'font-bold',
                    total.kind === 'credit' && 'text-status-paid',
                    total.kind === 'due' && doc.isLate && 'text-status-late',
                  )}
                >
                  {total.value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div
        id={remittanceId}
        className="invoice-print-remit mt-10 grid grid-cols-2 gap-8 border-t border-grey-200 pt-4 text-xs text-grey-600 scroll-mt-6"
      >
        <div>
          {payOnline && (
            /* ⚠ The document used to end in a routing number while the hosted
               page and the email both led with "Pay $X now". A PDF is the copy
               that leaves this app for good — forwarded, printed, filed — so it
               carries the offer too, and the bank block below is retitled
               "Prefer a bank transfer?" by the server. */
            <div className="invoice-print-pay mb-4">
              <a
                href={payOnline.url}
                className="inline-flex items-center rounded-lg bg-primary px-4 py-2 text-body-sm font-bold text-white no-underline transition-opacity hover:opacity-90"
              >
                {payOnline.label}
              </a>
              <p className="mt-1.5">{payOnline.note}</p>
              {payOnline.testNotice && (
                <p className="mt-0.5 italic text-grey-500">{payOnline.testNotice}</p>
              )}
            </div>
          )}
          <p className="font-semibold uppercase tracking-wider text-grey-500">
            {doc.remittance.heading}
          </p>
          <p className="mt-1">
            Make payment to{' '}
            <span className="font-semibold text-secondary">{doc.remittance.payee}</span> and
            reference invoice{' '}
            <span className="num font-semibold">#{doc.remittance.invoiceNumber}</span>.
          </p>
          {doc.remittance.rows.length > 0 && (
            <table className="mt-1.5">
              <tbody>
                {doc.remittance.rows.map((row) => (
                  <tr key={`${row.label ?? ''}${row.value}`} className="align-top">
                    {row.label ? (
                      <>
                        <td className="w-px whitespace-nowrap pr-3 font-semibold text-secondary">
                          {row.label}
                        </td>
                        <td className="num">
                          {row.value}
                          {copyRemittance && <CopyValue label={row.label} value={row.value} />}
                        </td>
                      </>
                    ) : (
                      <td colSpan={2} className="num">
                        {row.value}
                        {copyRemittance && <CopyValue label={null} value={row.value} />}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {doc.remittance.questionsEmail && (
            <p className="mt-1.5">
              Billing questions:{' '}
              {copyRemittance ? (
                /* On screen it is a way to ask; on paper it is an address. */
                <a
                  href={`mailto:${doc.remittance.questionsEmail}`}
                  className="font-semibold text-accent-700 hover:underline"
                >
                  {doc.remittance.questionsEmail}
                </a>
              ) : (
                doc.remittance.questionsEmail
              )}
            </p>
          )}
        </div>
        <div>
          <p className="font-semibold uppercase tracking-wider text-grey-500">Payment terms</p>
          <p className="mt-1">
            {doc.terms.label} — payment due by{' '}
            <span className="num font-semibold text-secondary">{doc.terms.dueDate}</span>.
            {doc.terms.extra ? ` ${doc.terms.extra}` : ''}
          </p>
          {doc.notes && (
            <>
              {/* The invoice's own note gets its own heading — it is not a
                  payment term and must not read as one. */}
              <p className="mt-3 font-semibold uppercase tracking-wider text-grey-500">
                {doc.notes.heading}
              </p>
              <p className="mt-1">{doc.notes.body}</p>
            </>
          )}
        </div>
      </div>
      <p className="mt-4 text-center text-xs text-grey-600">{doc.footer}</p>
    </>
  );
}
